import { Controller } from "@hotwired/stimulus"

const SEV_COLORS = { critical: "#dc2626", high: "#ea580c", medium: "#d97706", low: "#6b7280" }
const TYPE_ICONS = { ip: "fa-globe", domain: "fa-link", md5: "fa-hashtag", sha256: "fa-hashtag" }

export default class extends Controller {
  static targets = [
    "addForm", "tableBody", "searchInput", "stats", "formStatus",
    "typeInput", "valueInput", "severityInput", "sourceInput", "descriptionInput",
  ]
  static values = { url: String, logsUrl: String }

  connect() {
    this.allIocs = []
    this.loadIocs()
  }

  async loadIocs() {
    try {
      const resp = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      this.allIocs = await resp.json()
      this.renderTable(this.allIocs)
    } catch {
      this.tableBodyTarget.innerHTML = '<tr><td colspan="7" class="text-muted text-center">Failed to load IOCs</td></tr>'
    }
  }

  showAddForm() { this.addFormTarget.classList.remove("hidden") }
  hideAddForm() {
    this.addFormTarget.classList.add("hidden")
    this.clearForm()
  }

  async createIoc() {
    const ioc_type = this.typeInputTarget.value
    const value    = this.valueInputTarget.value.trim()
    const severity = this.severityInputTarget.value
    const source_name = this.sourceInputTarget.value.trim()
    const description = this.descriptionInputTarget.value.trim()

    if (!value || !source_name) {
      this.setStatus("Value and source name are required.", "error")
      return
    }

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf, Accept: "application/json" },
        body: JSON.stringify({ ioc: { ioc_type, value, severity, source_name, description } }),
      })
      if (resp.ok) {
        const created = await resp.json()
        this.allIocs.unshift(created)
        this.renderTable(this.filterBySearch(this.allIocs))
        this.updateStats()
        this.hideAddForm()
      } else {
        const data = await resp.json()
        this.setStatus((data.errors || ["Error saving IOC"]).join(", "), "error")
      }
    } catch {
      this.setStatus("Network error", "error")
    }
  }

  async deleteIoc(event) {
    const id = event.currentTarget.dataset.iocId
    if (!confirm("Remove this IOC from the library?")) return

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const url  = this.urlValue.replace(/\/$/, "") + `/${id}`
    try {
      await fetch(url, { method: "DELETE", headers: { "X-CSRF-Token": csrf } })
      this.allIocs = this.allIocs.filter(i => String(i.id) !== String(id))
      this.renderTable(this.filterBySearch(this.allIocs))
      this.updateStats()
    } catch { /* silent */ }
  }

  searchInLogs(event) {
    const value = event.currentTarget.dataset.iocValue
    const iocType = event.currentTarget.dataset.iocType
    const fieldMap = { ip: "source.ip", domain: "dns.question.name", md5: "file.hash.md5", sha256: "file.hash.sha256" }
    const field = fieldMap[iocType] || "source.ip"
    const query = `FROM logs | WHERE ${field} == "${value}"`
    const url = new URL(this.logsUrlValue, window.location.origin)
    url.searchParams.set("query", query)
    window.open(url.toString(), "_blank")
  }

  filterIocs() {
    const filtered = this.filterBySearch(this.allIocs)
    this.renderTable(filtered)
  }

  filterBySearch(iocs) {
    const q = this.searchInputTarget.value.toLowerCase().trim()
    if (!q) return iocs
    return iocs.filter(i =>
      i.value.toLowerCase().includes(q) ||
      i.ioc_type.toLowerCase().includes(q) ||
      (i.source_name || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q)
    )
  }

  renderTable(iocs) {
    this.updateStats(iocs)
    if (!iocs.length) {
      this.tableBodyTarget.innerHTML = '<tr><td colspan="7" class="text-muted text-center">No IOCs found</td></tr>'
      return
    }

    this.tableBodyTarget.innerHTML = iocs.map(ioc => {
      const color  = SEV_COLORS[ioc.severity] || "#6b7280"
      const icon   = TYPE_ICONS[ioc.ioc_type] || "fa-question"
      const added  = new Date(ioc.created_at).toLocaleDateString()
      return `
        <tr>
          <td><i class="fa ${icon}" style="color:${color}" title="${ioc.ioc_type}"></i> ${ioc.ioc_type}</td>
          <td class="siem-intel-value"><code>${this.escHtml(ioc.value)}</code></td>
          <td><span class="siem-intel-sev" style="color:${color}">${ioc.severity}</span></td>
          <td>${this.escHtml(ioc.source_name)}</td>
          <td class="siem-intel-desc text-muted">${this.escHtml(ioc.description || "")}</td>
          <td class="text-muted">${added}</td>
          <td class="siem-intel-actions">
            <button class="btn btn-xs btn-default siem-intel-search-btn"
                    title="Search in logs"
                    data-action="accounts--logs--intel#searchInLogs"
                    data-ioc-value="${this.escHtml(ioc.value)}"
                    data-ioc-type="${ioc.ioc_type}">
              <i class="fa fa-search"></i>
            </button>
            <button class="btn btn-xs btn-danger siem-intel-del-btn"
                    title="Remove IOC"
                    data-action="accounts--logs--intel#deleteIoc"
                    data-ioc-id="${ioc.id}">
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>`
    }).join("")
  }

  updateStats(iocs = this.allIocs) {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 }
    iocs.forEach(i => { if (counts[i.severity] !== undefined) counts[i.severity]++ })
    this.statsTarget.innerHTML = `
      <span class="siem-intel-stat" style="color:#dc2626">${counts.critical} critical</span>
      <span class="siem-intel-stat" style="color:#ea580c">${counts.high} high</span>
      <span class="siem-intel-stat" style="color:#d97706">${counts.medium} medium</span>
      <span class="siem-intel-stat" style="color:#6b7280">${counts.low} low</span>
      <span class="siem-intel-stat text-muted">${iocs.length} total</span>`
  }

  clearForm() {
    [this.valueInputTarget, this.sourceInputTarget, this.descriptionInputTarget].forEach(el => { el.value = "" })
    this.formStatusTarget.textContent = ""
  }

  setStatus(msg, type) {
    this.formStatusTarget.textContent = msg
    this.formStatusTarget.style.color = type === "error" ? "#dc2626" : "#059669"
  }

  escHtml(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
